/**
 * Meetverse pill rail (Live Layer v2, item 2) — the live layer above the
 * meetverse feed. ONE `LivePillRow` (Dexter's ruling, 2026-07-27: "we should
 * only show one" row of event pills — PR #1035 widened live discovery to
 * upcoming events, which left the personal + public rows both non-empty most
 * of the time on the authed Meetverse tab) merges TWO sources into one list,
 * then re-tiers the WHOLE thing with `compareEntriesByPhaseAndGoingOnly` — the
 * STABLE variant of the shared rule (order: see `compareEntries`):
 *
 *   1. PERSONAL (authed only, `meetverse_personal_pill`): the viewer's OWN
 *      upcoming/soon/live events from `useLiveEventsRail()` — the SAME
 *      `eventsLiveRailKey()` cache the Home rail reads, so the shared KEY dedupes
 *      warm (within `railStaleTimeMs`) and concurrent reads: a session that
 *      already loaded the Home rail serves this from cache. It is NOT one fetch
 *      per session — a meetverse-first authed session (persisted tab) is the
 *      first reader and cold-fetches `GET /events/my-calendars` here, and either
 *      surface refetches once `railStaleTimeMs` elapses and it remounts. The hook
 *      is auth-gated internally (my-calendars 401s for guests), so these entries
 *      are empty signed-out. Already `compareEntries`-ordered upstream of this
 *      component — my-calendars is NOT going-filtered, so a personal entry's
 *      `is_going` varies event by event and it can land in a going or a
 *      not-going group.
 *      Trimmed here to `LIVE_EVENTS_CONFIG.railUpcomingMax` far-future entries
 *      (`capRailForDisplay`, the same display cap the Home rail applies) BEFORE the merge; live and soon personal entries are never
 *      trimmed.
 *   2. PUBLIC discovery (guests + members, `meetverse_pill`): `useMeetverseLiveEvents()`
 *      (GET /events/live in TWO calls — the live+soon page, then the upcoming
 *      band on its own so a busy live set can't starve it — merged live → soon
 *      → upcoming and UNCAPPED by the hook, Dexter 2026-07-27), reduced to
 *      public-only entries here (see the two filters below), then capped to
 *      `LIVE_EVENTS_CONFIG.meetverseUpcomingMax` upcoming entries — the cap
 *      runs LAST, on what survives the filters. The backend ranks live rows
 *      busiest-room-first on purpose (`useMeetverseLiveEvents`'s own doc) —
 *      that order is preserved untouched through the filters and the cap.
 *      Every public entry is `is_going === false` by construction (the
 *      own-event filter below drops any that aren't), so a public entry only
 *      ever joins a not-going group.
 *
 * Guests therefore see the public discovery only — live + soon + up to 3
 * upcoming entries, in pure phase order (nothing is ever `is_going` signed out,
 * so the going key never fires); authed viewers see BOTH sources merged as ONE
 * row:
 * `merged = [...personalEntries, ...publicOnly].sort(compareEntriesByPhaseAndGoingOnly)`
 * (Dexter's "Keep server ranking" ruling, 2026-07-28, fresh-eyes review finding
 * I3). This enforces ONLY the phase and going boundaries; the merge does NOT
 * collapse to "your stuff always first" — a public discovery live entry (always
 * not-going) is outranked only by the viewer's own GOING LIVE pill, and beats
 * every soon and upcoming pill they hold. WITHIN one (phase, going) group the
 * pre-sort concatenation order survives untouched (`Array.prototype.sort` is
 * stable): personal entries lead in the rail's own order, then public entries
 * fill the rest in the discovery payload's own order. An EARLIER version ran the
 * full `compareEntries` (… → start) across the merge, which laundered the
 * backend's deliberate busiest-first live ranking through a client start-time
 * sort. See the "order" describe block in this component's test file for the
 * pins. NO live volume cap here, deliberately: Home bounds its live block
 * (`railLiveMax`), the discovery row does not.
 *
 * Capping LAST is load-bearing (Dexter's review of PR #1035): while the cap
 * lived in the hook it ran BEFORE the filters, so a member whose personal
 * rail held the 3 nearest upcoming events was left with an EMPTY public
 * upcoming set while upcoming rows 4-6 sat discarded in the same payload —
 * the engaged member got strictly less discovery than a guest on identical
 * data. Filtering first and capping after keeps 3 in every case, at no extra
 * cost: the rows are already in the payload. The cap runs on the PAYLOAD's
 * server order, BEFORE the re-tier below it, so which 3 upcoming entries
 * survive is unaffected by that re-tier.
 *
 * The merge does NOT blur the two funnels (spec §9): `LivePillRow`'s
 * `surfaceFor` override resolves the analytics surface PER ENTRY, so a
 * personal pill still fires `meetverse_personal_pill` and a public pill still
 * fires `meetverse_pill` — for both `live_surface_displayed` and
 * `live_surface_tapped` — inside the one merged row, wherever the sort places
 * it. Posts counts ride `LivePillRow`'s own internal `useEventFeedCounts`
 * batch (T-8) over the merged row's live ids: one row, one batch, so the
 * hoisted batch this rail used to carry (to guarantee ONE request across two
 * rows) is no longer needed here. Counts carry no auth gate (2026-07-12
 * ruling — the count is the event's universal activity number, the "N posts"
 * conversion tap bait), so a guest pill carries the same posts suffix an
 * authed one does. Neither source has any dismissal.
 *
 * SwipeRow's front-reset: post-mount, this row's horizontal scroll resets ONLY
 * on an observed not-live→live transition at the front — `LivePillRow` forwards
 * a `useLiveFrontResetKey` token as `startKey`; see that hook for the rule. It
 * matters most on THIS surface: the merged row's front slot is the churny
 * busiest public live room, so "reset whenever the front is live" fired on every
 * refetch and every ended room. Mount still asserts scroll 0 regardless.
 *
 * A11y note (fresh-eyes review, finding M5): the two group NAMES did not
 * survive the merge the way the two surfaces did. Pre-merge, the personal and
 * public rows carried distinct `ariaLabel`s ('Your events' / 'Live and
 * upcoming events') so a screen-reader user could tell their own events from
 * public discovery at the GROUP level; post-merge there is one row, so it
 * takes `LivePillRow`'s one default label. This is a conscious trade of
 * Dexter's one-row ruling, not an oversight — every pill still names its own
 * event, phase and going count individually (`role="status"`), so no
 * per-artifact information is lost, only the group-level distinction.
 *
 * The row is an isolated leaf mounted above the feed (Home.tsx): each data
 * hook owns its own `useNow` tick, so this bounded subtree re-renders every
 * 30s and the feed never does (spec §3, leaf-tick isolation). The personal
 * hook's tick is gated off for guests (a disabled `useNow` mounts no timer).
 */
import { LIVE_EVENTS_CONFIG } from '@config/liveEvents';
import {
  capRailForDisplay,
  compareEntriesByPhaseAndGoingOnly,
  useLiveEventsRail,
  type LiveRailEntry,
} from '@hooks/useLiveEventsRail';
import { useMeetverseLiveEvents } from '@hooks/useMeetverseLiveEvents';
import type { LiveSurface } from '@lib/analytics-events';
import { LivePillRow } from './LivePillRow';

/** The public discovery entries — the live-first discovery funnel (guests + members). */
const PUBLIC_SURFACE: LiveSurface = 'meetverse_pill';
/** The member's own entries — their own surface so they never inflate the discovery funnel. */
const PERSONAL_SURFACE: LiveSurface = 'meetverse_personal_pill';

export function LiveMeetversePills() {
  // The viewer's own entries (auth-gated inside the hook; empty for guests),
  // reading the same eventsLiveRailKey() cache as the Home rail — the shared
  // key dedupes warm/concurrent reads (NOT one fetch per session: a
  // meetverse-first session cold-fetches here, and either surface refetches
  // past railStaleTimeMs). `now` is intentionally not read here — see the
  // single merged-row tick below.
  const { entries: railEntries } = useLiveEventsRail();
  // The personal block's own far-future trim, applied BEFORE the merge — the
  // same `railUpcomingMax` cap the Home rail uses, for the same reason (the rail
  // fetches 10 rows so the client can see past a page of live rooms; without a
  // cap a full calendar would push discovery off the row). NO `liveMax` here:
  // Home bounds its live block, this row deliberately does not — the meetverse
  // IS the discovery surface, so a long live block is the product (Dexter's
  // ruling, 2026-07-29). The public tail keeps its own `meetverseUpcomingMax`
  // cap below, unchanged.
  const personalEntries = capRailForDisplay(railEntries, {
    upcomingMax: LIVE_EVENTS_CONFIG.railUpcomingMax,
  });
  // The public discovery list (guests + members).
  const { entries: publicEntries, now: publicNow } = useMeetverseLiveEvents();

  // The public entries are built in THIS order — two filters, then the cap:
  //
  //   1. id dedupe against the personal rail — an event on both surfaces
  //      renders once, as the personal pill (never twice on one screen);
  //   2. `is_going` filter — the viewer's OWN events never masquerade as public
  //      discovery. The dedupe alone cannot do this: the personal rail is one
  //      page (`LIVE_RAIL_PER_PAGE`), further trimmed for display above, so a
  //      member's own event past that depth is not in `personalIds` at all and
  //      used to leak into the discovery row — firing `meetverse_pill`
  //      impressions/taps for an own event, the exact funnel contamination the
  //      distinct surfaces exist to prevent (Dexter's review of PR #1035).
  //      `is_going` marks the viewer's own going events at ANY depth; a guest's
  //      rows are all `is_going` false, so guests are untouched. NOTE (PR #1040
  //      review): a personal entry the display cap above trimmed is not in
  //      `personalIds` either, so a joined-space event the member is NOT going
  //      to can reappear here as a discovery pill under `meetverse_pill` — the
  //      same behavior the page cap has always had, and correct attribution
  //      (a not-going joined-space room IS discovery). The SURFACE LABEL moves
  //      with the cap, not just the pill's position: an entry that rendered as
  //      `meetverse_personal_pill` before the cap existed now renders as
  //      `meetverse_pill` once it falls past `railUpcomingMax`, so a funnel
  //      comparison across that change is not like-for-like. Their own GOING
  //      events still never leak, at any depth, because of this filter;
  //   3. the upcoming cap, LAST — see the docblock: capping before the filters
  //      let a member's personal overlap empty the public upcoming set while
  //      further upcoming rows sat unused in the payload. Live and soon entries
  //      are never capped.
  const personalIds = new Set(personalEntries.map(entry => entry.event.id));
  const publicOnly: LiveRailEntry[] = [];
  let upcomingKept = 0;
  for (const entry of publicEntries) {
    if (personalIds.has(entry.event.id) || entry.event.is_going === true) {
      continue;
    }
    if (entry.phase === 'upcoming') {
      if (upcomingKept >= LIVE_EVENTS_CONFIG.meetverseUpcomingMax) {
        continue;
      }
      upcomingKept += 1;
    }
    publicOnly.push(entry);
  }

  // ONE merged list, re-tiered by PHASE AND GOING ONLY — Dexter's "Keep server
  // ranking" ruling, 2026-07-28. NOT the rail's full `compareEntries`: within one (phase,
  // going) group the concatenation order survives untouched, so personal
  // entries (already rail-ordered) lead and public entries (server-ordered,
  // busiest-live-first) fill the rest — see the docblock and
  // `compareEntriesByPhaseAndGoingOnly`'s own doc for why. Nothing caps or
  // re-slots on top: live entries lead the row by definition, so there is no
  // agenda above them left to protect.
  const merged = [...personalEntries, ...publicOnly].sort(compareEntriesByPhaseAndGoingOnly);

  return (
    <LivePillRow
      entries={merged}
      // Both hooks tick on the same 30s cadence off the same wall clock, so
      // either `now` keeps every entry's countdown accurate; `publicNow` is
      // the one consistent choice for the single merged row because it also
      // covers guests, whose personal tick is disabled.
      now={publicNow}
      surface={PUBLIC_SURFACE}
      surfaceFor={entry => (personalIds.has(entry.event.id) ? PERSONAL_SURFACE : PUBLIC_SURFACE)}
    />
  );
}
